import { createPopper } from '@popperjs/core';
import { createFocusTrap } from 'focus-trap';
import { findParent, getPostThumbnailURL, getYoutubeThumbnailURL, isURLExternal } from './utils';

export default class SearchPopper {

	constructor(element) {
		this.handleToggleClick = this.handleToggleClick.bind(this);
		this.handleSearchChange = this.handleSearchChange.bind(this);
		this.handleResultsGroupLoadMoreClick = this.handleResultsGroupLoadMoreClick.bind(this);
		this.handleCloseButtonClick = this.handleCloseButtonClick.bind(this);

		this.element = element;
		this.input = this.element.querySelector('.gigs-search-popper__input');
		this.resultTemplate = this.element.querySelector('.gigs-search-popper__result[data-is-template="true"]');
		this.resultGroups = this.element.querySelectorAll('.gigs-search-popper__results-group');
		this.resultsGroupLoadMore = Array.from(this.resultGroups || []).map(resultGroup => {
			return resultGroup.querySelector('.gigs-search-popper__results-group-load-more');
		});
		this.searchables = Array.from(this.resultGroups || []).map(resultGroup => {
			return {
				type: resultGroup.dataset.type,
				restBase: resultGroup.dataset.restBase,
				abortController: null,
				results: [],
				error: null,
				isLoading: false,
				total: 0,
				page: 1,
			};
		});
		this.closeButton = this.element.querySelector('.gigs-search-popper__close');
		this.toggle = document.getElementById('search-toggle');
		this.totalResultsElement = this.element.querySelector('.gigs-search-popper__footer-total > i');
		this.popperInstance = null;
		this.searchTimer = null;
		this.wpAdminBar = document.getElementById('wpadminbar');
		this.focusableElements = [this.element];
		if (this.wpAdminBar) this.focusableElements.push(this.wpAdminBar);
	}

	init() {
		this.initPopper();
		this.initFocusTrap();
		this.toggle.addEventListener('click', this.handleToggleClick);
		this.input.addEventListener('input', this.handleSearchChange);
		this.closeButton.addEventListener('click', this.handleCloseButtonClick);
		this.resultsGroupLoadMore.forEach(loader => {
			loader.addEventListener('click', this.handleResultsGroupLoadMoreClick);
		});
	}

	destroy() {
		this.clearAll();
		this.destroyFocusTrap();
		this.destroyPopper();
		this.toggle.removeEventListener('click', this.handleToggleClick);
		this.input.removeEventListener('input', this.handleSearchChange);
		this.closeButton.removeEventListener('click', this.handleCloseButtonClick);
		this.resultsGroupLoadMore.forEach(loader => {
			loader.removeEventListener('click', this.handleResultsGroupLoadMoreClick);
		});
	}

	initPopper() {
		const arrow = this.appendArrow();
		this.popperInstance = createPopper(this.toggle, this.element, {
			placement: 'bottom-end',
			strategy: 'fixed',
			modifiers: [
				{
					name: 'preventOverflow',
					options: {
						mainAxis: true,
						altAxis: true,
					},
				},
				{
					name: 'arrow',
					options: {
						element: arrow,
					},
				},
				{
					name: 'offset',
					options: {
						offset: [20, 20],
					},
				},
			],
		});
	}

	destroyPopper() {
		if (this.popperInstance) {
			this.popperInstance.destroy();
			this.popperInstance = null;
		}
	}

	initFocusTrap() {
		this.focusTrap = createFocusTrap(this.focusableElements, {
			clickOutsideDeactivates: true,
			allowOutsideClick: true,
			onDeactivate: () => this.close(),
		});
	}

	destroyFocusTrap() {
		if (this.focusTrap) {
			this.focusTrap.deactivate();
			this.focusTrap = null;
		}
	}

	appendArrow() {
		const arrow = document.createElement('i');
		arrow.setAttribute('data-popper-arrow', true);
		this.element.appendChild(arrow);
		return arrow;
	}

	handleToggleClick(e) {
		e.preventDefault();
		this.open();
	}

	open() {
		this.clearAll();
		this.element.hidden = false;
		this.popperInstance.forceUpdate();
		this.focusTrap.activate();
	}

	close() {
		this.clearAll();
		this.element.hidden = true;
		this.focusTrap.deactivate();
	}

	handleCloseButtonClick(e) {
		e.preventDefault();
		this.close();
	}

	handleSearchChange(e) {
		const value = (e.currentTarget instanceof HTMLElement) ? e.currentTarget.value : '';
		if (this.searchTimer) window.clearTimeout(this.searchTimer);
		if (value) {
			this.searchTimer = window.setTimeout(() => this.searchAll(value), 500);
		} else {
			this.clearAll();
		}
	}

	handleResultsGroupLoadMoreClick(e) {
		const parentResultsGroup = this.findParentResultsGroup(e.currentTarget);
		const searchable = this.findSearchable(parentResultsGroup.dataset.type, parentResultsGroup.dataset.restBase);
		this.searchMore(searchable);
	}

	findParentResultsGroup(element) {
		return findParent(element, (parent) => {
			return parent.classList.contains('gigs-search-popper__results-group');
		});
	}

	getPostThumbnailURL(post) {
		switch (post.type) {
			case 'gigs_event': {
				const postThumbnail = getPostThumbnailURL(post, 'thumbnail');
				if (postThumbnail) {
					return postThumbnail;
				} else if (post.meta.gigs_event_youtube_id) {
					return getYoutubeThumbnailURL(post.meta.gigs_event_youtube_id);
				} else {
					return window.gigs.shareImageURL;
				}
			}
			default: {
				const postThumbnail = getPostThumbnailURL(post, 'thumbnail');
				if (postThumbnail) {
					return postThumbnail;
				} else {
					return window.gigs.shareImageURL;
				}
			}
		}
	}

	getTermThumbnailURL(term) {
		switch (term.taxonomy) {
			default: {
				return window.gigs.shareImageURL;
			}
		}
	}

	findGroup(searchable) {
		return this.element.querySelector(`.gigs-search-popper__results-group[data-type="${searchable.type}"][data-rest-base="${searchable.restBase}"]`);
	}

	findSearchable(type, restBase) {
		return this.searchables.find((searchable) => ((searchable.type === type) && (searchable.restBase === restBase)));
	}

	clearAll() {
		if (this.searchTimer) window.clearTimeout(this.searchTimer);
		this.input.value = '';
		for (const [i] of this.searchables.entries()) {
			if (this.searchables[i].abortController) this.searchables[i].abortController.abort();
			this.searchables[i].abortController = null;
			this.searchables[i].error = null;
			this.searchables[i].results = [];
			this.searchables[i].total = 0;
			this.searchables[i].page = 1;
			this.searchables[i].isLoading = false;
			this.setDataAttributes(true);
		}
		this.render();
	}

	async searchAll(search) {
		const promises = [];
		for (const searchable of this.searchables) {
			promises.push(this.searchGroup(searchable, search, true));
		}
		await Promise.all(promises);
		this.render();
	}

	async searchMore(searchable) {
		const search = this.input.value;
		await this.searchGroup(searchable, search, false);
		this.render();
	}

	setDataAttributes(detectIsLoading = true) {
		// Global attributes.
		const isResultsVisible = (this.searchables.findIndex((searchable) => ((searchable.total > 0) || Boolean(searchable.error))) >= 0);
		this.element.dataset.isResultsVisible = isResultsVisible;
		this.element.dataset.isDirty = Boolean(this.input.value);
		if (detectIsLoading) {
			const isLoading = (this.searchables.findIndex((searchable) => searchable.isLoading) >= 0);
			this.element.dataset.isLoading = isLoading;
		}
		// Group attributes.
		this.searchables.forEach((searchable) => {
			const resultsGroup = this.findGroup(searchable);
			const loadMore = resultsGroup.querySelector('.gigs-search-popper__results-group-load-more');
			resultsGroup.dataset.isVisible = ((searchable.total > 0) || Boolean(searchable.error));
			resultsGroup.dataset.isLoading = searchable.isLoading;
			resultsGroup.dataset.isPaginationComplete = (searchable.results.length >= searchable.total);
			loadMore.disabled = searchable.isLoading;
		});
		this.popperInstance.forceUpdate();
	}

	getTotalResults() {
		return this.searchables
			.map((searchable) => searchable.total)
			.reduce((prevTotal, groupTotal) => (prevTotal + groupTotal));
	}

	render() {
		// Global renders.
		const totalResults = this.getTotalResults();
		this.totalResultsElement.innerHTML = totalResults.toLocaleString('en-US');

		// Searchable renders.
		for (const searchable of this.searchables) {

			const resultsGroup = this.findGroup(searchable);
			const resultsGroupResults = resultsGroup.querySelector('.gigs-search-popper__results-group-results');
			const resultsGroupError = resultsGroup.querySelector('.gigs-search-popper__results-group-error');
			const resultsGroupTotal = resultsGroup.querySelector('.gigs-search-popper__results-group-title > i');
			resultsGroupResults.innerHTML = '';
			resultsGroupError.innerHTML = '';
			resultsGroupTotal.innerHTML = '(0)';

			if (searchable.error) {

				resultsGroupError.innerHTML = searchable.error.message;

			} else {

				resultsGroupTotal.innerHTML = `(${searchable.total.toLocaleString('en-US')})`;

				searchable.results.forEach(result => {

					const template = this.resultTemplate.cloneNode(true);
					const title = template.querySelector('.gigs-search-popper__result-title');
					const excerpt = template.querySelector('.gigs-search-popper__result-excerpt');
					const image = template.querySelector('.gigs-search-popper__result-image');

					const url = result.link || '#';

					template.removeAttribute('data-is-template');
					template.removeAttribute('hidden');
					template.href = url;

					if (isURLExternal(url)) {
						template.target = '_blank';
						template.rel = 'noopener noreferrer';
					}

					switch (searchable.type) {
						case 'post-type':
							title.innerHTML = result.title ? result.title.rendered : '';
							excerpt.innerHTML = result.excerpt ? result.excerpt.rendered : '';
							image.src = this.getPostThumbnailURL(result);
							image.alt = title.innerText;
							break;
						case 'taxonomy':
							title.innerHTML = result.name || '';
							excerpt.innerHTML = result.description ? `<p>${result.description}</p>` : '';
							image.src = this.getTermThumbnailURL(result);
							image.alt = title.innerText;
							break;
					}

					resultsGroupResults.appendChild(template);

				});
			}
		}
		this.popperInstance.forceUpdate();
	}

	async searchGroup(searchable, search, reset = true) {
		// Find this groups state object.
		const i = this.searchables.findIndex((s) => ((s.type === searchable.type) && (s.restBase === searchable.restBase)));

		// Find abort controller and abort.
		if (i === -1) throw new Error(`Searchable of type "${searchable.type}" and rest base of "${searchable.restBase}" not found`);
		if (this.searchables[i].abortController) this.searchables[i].abortController.abort();

		// Reset state.
		this.searchables[i].abortController = new AbortController();
		this.searchables[i].error = null;
		this.searchables[i].isLoading = true;
		if (reset) {
			this.searchables[i].results = [];
			this.searchables[i].total = 0;
			this.searchables[i].page = 1;
		}
		this.setDataAttributes(reset);

		// Begin request.
		try {
			const { total, results } = await this.fetchRestItems(searchable, search, this.searchables[i].abortController.signal);
			this.searchables[i].abortController = null;
			this.searchables[i].total = total;
			this.searchables[i].isLoading = false;
			this.searchables[i].results = this.searchables[i].results.concat(results);
			this.searchables[i].page = (this.searchables[i].page + 1);
			this.setDataAttributes(reset);
		} catch (error) {
			if (error.name !== 'AbortError') {
				console.error(error);
				this.searchables[i].abortController = null;
				this.searchables[i].error = error;
				this.searchables[i].isLoading = false;
				this.setDataAttributes(reset);
			}
		}
	}

	async fetchRestItems(searchable, search, signal) {
		const params = new URLSearchParams({
			search: search,
			page: searchable.page,
			per_page: 3,
			_embed: true,
		});
		if (searchable.type === 'post-type') {
			params.set('orderby', 'relevance');
			params.set('order', 'desc');
		}
		if (searchable.type === 'taxonomy') {
			params.set('hide_empty', true);
			params.set('orderby', 'count');
			params.set('order', 'desc');
		}
		const url = `/wp-json/wp/v2/${searchable.restBase}?${params.toString()}`;
		const response = await fetch(url, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			},
			signal: signal,
		});
		const data = await response.json();
		if (response.ok) {
			return {
				total: Number(response.headers.get('X-WP-Total')),
				results: data,
			};
		} else {
			throw new Error(data.message || `There was a problem fetching data from ${url}`);
		}
	}
}
